import React from 'react';
import { Document, Page, View, Text, StyleSheet } from '@react-pdf/renderer';

const fmtDate = (d) => {
  if (!d) return '—';
  const s = d.slice(0, 10);
  const [y, m, day] = s.split('-');
  return day && m && y ? day + '/' + m + '/' + y : '—';
};
const fmtSize = (bytes) => {
  if (!bytes) return '—';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / 1048576).toFixed(1) + ' MB';
};

const s = StyleSheet.create({
  page: { paddingTop: 71, paddingBottom: 71, paddingLeft: 40, paddingRight: 40, fontSize: 8, fontFamily: 'Helvetica', color: '#1a1a2e' },
  headerBar: { position: 'absolute', top: 0, left: 0, right: 0, height: 5, backgroundColor: '#2EA084' },
  headerWrap: { position: 'absolute', top: 12, left: 40, right: 40, flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-end', paddingBottom: 8, borderBottom: '1pt solid #e9ecef' },
  headerLeft: { fontSize: 10, fontFamily: 'Helvetica-Bold', color: '#2EA084' },
  headerRight: { fontSize: 8, color: '#868e96', textAlign: 'right' },
  footer: { position: 'absolute', bottom: 24, left: 40, right: 40, flexDirection: 'row', justifyContent: 'space-between', fontSize: 7, color: '#868e96', borderTop: '0.5pt solid #e9ecef', paddingTop: 6 },
  sectionHead: { borderLeft: '3pt solid #2EA084', paddingLeft: 8, paddingTop: 4, paddingBottom: 4, fontSize: 9, fontFamily: 'Helvetica-Bold', color: '#0A1628', backgroundColor: '#fff', marginBottom: 6, marginTop: 14 },
  kpiRow: { flexDirection: 'row', marginBottom: 8 },
  kpiBox: { border: '1pt solid #e9ecef', borderRadius: 4, padding: '8 10', flex: 1, margin: '0 4' },
  kpiLabel: { fontSize: 7, color: '#868e96', textTransform: 'uppercase', marginBottom: 2 },
  kpiValue: { fontSize: 12, fontFamily: 'Helvetica-Bold' },
  tableHead: { flexDirection: 'row', backgroundColor: '#f8f9fa', borderBottom: '1pt solid #e9ecef', padding: '4 6', fontFamily: 'Helvetica-Bold', fontSize: 7 },
  tableRow: { flexDirection: 'row', borderBottom: '0.5pt solid #e9ecef', padding: '4 6', fontSize: 8 },
  tableRowAlt: { flexDirection: 'row', borderBottom: '0.5pt solid #e9ecef', padding: '4 6', fontSize: 8, backgroundColor: '#f8f9fa' },
  groupHead: { borderLeft: '3pt solid #2EA084', paddingLeft: 8, paddingTop: 3, paddingBottom: 3, fontSize: 8, fontFamily: 'Helvetica-Bold', color: '#0A1628', marginTop: 8, marginBottom: 2 },
});

export default function DocumentiTabPDF({ documenti, cantiere, azienda }) {
  const oggi = new Date().toLocaleDateString('it-IT');
  const docs = documenti || [];

  // Breakdown per tipo
  const perTipo = {};
  docs.forEach(d => {
    const t = d.tipo || 'altro';
    perTipo[t] = (perTipo[t] || 0) + 1;
  });
  const ultimoUpload = docs.length > 0
    ? docs.reduce((latest, d) => (d.data_upload || '') > (latest.data_upload || '') ? d : latest, docs[0])
    : null;

  // Raggruppamento per tipo
  const grouped = {};
  docs.forEach(d => {
    const t = (d.tipo || 'altro').charAt(0).toUpperCase() + (d.tipo || 'altro').slice(1);
    if (!grouped[t]) grouped[t] = [];
    grouped[t].push(d);
  });

  return (
    <Document>
      <Page size="A4" style={s.page}>
        <View style={s.headerBar} fixed />
        <View style={s.headerWrap} fixed>
          <Text style={s.headerLeft}>Documenti — {cantiere?.titolo || ''}</Text>
          <View style={s.headerRight}>
            <Text>{azienda?.nome || ''}</Text>
            <Text>Generato il {oggi}</Text>
          </View>
        </View>

        <Text style={s.sectionHead}>Riepilogo</Text>
        <View style={s.kpiRow}>
          <View style={s.kpiBox}>
            <Text style={s.kpiLabel}>Totale documenti</Text>
            <Text style={s.kpiValue}>{docs.length}</Text>
          </View>
          <View style={s.kpiBox}>
            <Text style={s.kpiLabel}>Tipi</Text>
            <Text style={{ fontSize: 8 }}>{Object.entries(perTipo).map(([k, v]) => k + ': ' + v).join(', ')}</Text>
          </View>
          <View style={s.kpiBox}>
            <Text style={s.kpiLabel}>Ultimo caricamento</Text>
            <Text style={{ fontSize: 8 }}>{ultimoUpload ? fmtDate(ultimoUpload.data_upload) : '—'}</Text>
          </View>
        </View>

        <Text style={s.sectionHead}>Lista documenti</Text>
        {Object.entries(grouped).map(([tipo, lista]) => (
          <View key={tipo}>
            <Text style={s.groupHead}>{tipo} ({lista.length})</Text>
            <View style={s.tableHead}>
              <Text style={{ flex: 4 }}>Nome file</Text>
              <Text style={{ flex: 1.5 }}>Tipo</Text>
              <Text style={{ flex: 1.5 }}>Provenienza</Text>
              <Text style={{ flex: 1.5 }}>Data</Text>
              <Text style={{ flex: 1, textAlign: 'right' }}>Dimensione</Text>
            </View>
            {lista.map((d, i) => (
              <View key={d.id} style={i % 2 === 0 ? s.tableRow : s.tableRowAlt}>
                <Text style={{ flex: 4 }}>{d.nome_file || '—'}</Text>
                <Text style={{ flex: 1.5 }}>{d.tipo || '—'}</Text>
                <Text style={{ flex: 1.5 }}>{d.provenienza || '—'}</Text>
                <Text style={{ flex: 1.5 }}>{fmtDate(d.data_upload)}</Text>
                <Text style={{ flex: 1, textAlign: 'right' }}>{fmtSize(d.dimensione_bytes)}</Text>
              </View>
            ))}
          </View>
        ))}

        <View style={s.footer} fixed>
          <Text>mAIstro — Documenti</Text>
          <Text>{cantiere?.titolo || ''}</Text>
          <Text render={({ pageNumber, totalPages }) => 'Pagina ' + pageNumber + ' di ' + totalPages} />
        </View>
      </Page>
    </Document>
  );
}
